// components
import { Button } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import EntitySearch from 'components/mrp/components/EntitySearch';
import { Popover } from 'components/mrp/components/Popover';

// stores/context
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useCreateShortcutContext } from 'components/shared/ShortcutProvider';
import { ShortcutContext } from 'components/shared/ShortcutProvider/ShortcutContext';
import { GLOBAL_CONTEXT } from 'components/shared/ShortcutProvider/ShortcutProvider.consts';
import { useShallow } from 'zustand/shallow';
import { usePopoverFocus } from 'hooks/usePopoverFocus';

// types
import type { PlanningEntityIdentifier } from 'generated/index';

// react/utils
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const OpenCalendarButton = () => {
  'use memo';
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const { entityIdsPerCalendarId, openCalendar } = useCalendarStore(
    useShallow((state) => ({
      entityIdsPerCalendarId: state.entityIdsPerCalendarId,
      openCalendar: state.control.openCalendar
    }))
  );

  const openCalendarEntityIds = [
    ...new Set(Object.values(entityIdsPerCalendarId).flat())
  ];

  const {
    isOpen: isOpenCalendarPopoverOpen,
    open: handleOpen,
    close: closeCalendarPopover,
    onExited: handleExited
  } = usePopoverFocus();

  const { setActiveShortcutContexts, shortcuts } = useContext(ShortcutContext);

  useEffect(() => {
    setActiveShortcutContexts?.(
      !!isOpenCalendarPopoverOpen ? [] : Object.keys(shortcuts)
    );
  }, [isOpenCalendarPopoverOpen, setActiveShortcutContexts, shortcuts]);

  useCreateShortcutContext(GLOBAL_CONTEXT, [
    {
      name: 'addCalendar',
      label: t('shortcuts.addCalendar.label'),
      description: t('shortcuts.addCalendar.description'),
      callback: handleOpen,
      activatorKey: 'O',
      controllerKeys: ['PRIMARY']
    }
  ]);

  // listen for shortcuts
  useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      // NOTE: close the add calendar when Escape is pressed
      if (event.code === 'Escape') {
        event.preventDefault();
        closeCalendarPopover();
      }
    };

    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [closeCalendarPopover]);

  const onChange = (
    planningEntityIdentifier: PlanningEntityIdentifier,
    label: string
  ) => {
    openCalendar(
      [
        {
          label,
          planningEntityIdentifier
        }
      ],
      true
    );

    closeCalendarPopover();
  };

  return (
    <Popover
      id="entity-search-popover"
      useForcedState={true}
      forceOpen={isOpenCalendarPopoverOpen}
      onClose={closeCalendarPopover}
      onExited={handleExited}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom'
      }}
      component={
        <EntitySearch
          activeEntities={openCalendarEntityIds}
          minWidth={400}
          onChange={onChange}
        />
      }
    >
      <Button onClick={handleOpen} startIcon={<Icon type="calendar-week" />}>
        {t('addCalendar')}
      </Button>
    </Popover>
  );
};

export default OpenCalendarButton;
